import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { exportAdhesionsToExcel } from '../utils/excelExport';
import { 
  MagnifyingGlassIcon,
  UserIcon,
  PencilIcon,
  XMarkIcon,
  ClipboardIcon,
  EyeIcon,
  FunnelIcon,
  CheckIcon,
  BuildingOfficeIcon,
  DocumentArrowDownIcon,
  EnvelopeIcon,
  PhoneIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';

export default function SuperAdminAllUsers() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [orphanAdhesions, setOrphanAdhesions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showWithoutAdhesionOnly, setShowWithoutAdhesionOnly] = useState(false);
  const [showAdhesionsUserNotFound, setShowAdhesionsUserNotFound] = useState(false);
  const [showUsersOnly, setShowUsersOnly] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Nouveaux filtres comme dans Users.js
  const [statusFilter, setStatusFilter] = useState([]);
  const [yearFilter, setYearFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState([]);
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [showYearFilter, setShowYearFilter] = useState(false);
  const [showServiceFilter, setShowServiceFilter] = useState(false);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Charger les filtres sauvegardés au montage
  useEffect(() => {
    const savedFilters = localStorage.getItem('superadmin_users_filters');
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        setStatusFilter(filters.statusFilter || []);
        setYearFilter(filters.yearFilter || '');
        setServiceFilter(filters.serviceFilter || []);
        setSearchTerm(filters.searchTerm || '');
        setShowWithoutAdhesionOnly(filters.showWithoutAdhesionOnly || filters.showOrphanOnly || false);
        setShowAdhesionsUserNotFound(filters.showAdhesionsUserNotFound || false);
        setShowUsersOnly(filters.showUsersOnly !== undefined ? filters.showUsersOnly : true);
      } catch (error) {
        console.error('Erreur lors du chargement des filtres:', error);
      }
    }
    setFiltersLoaded(true);
  }, []);

  // Sauvegarder les filtres à chaque modification
  useEffect(() => {
    if (!filtersLoaded) return;
    
    const filters = {
      statusFilter,
      yearFilter,
      serviceFilter,
      searchTerm,
      showWithoutAdhesionOnly,
      showAdhesionsUserNotFound,
      showUsersOnly
    };
    localStorage.setItem('superadmin_users_filters', JSON.stringify(filters));
  }, [statusFilter, yearFilter, serviceFilter, searchTerm, showWithoutAdhesionOnly, showAdhesionsUserNotFound, showUsersOnly, filtersLoaded]);

  useEffect(() => {
    if (!user || user.role !== 'super_admin') {
      navigate('/');
      return;
    }

    fetchAllUsers();
  }, [user, navigate]);

  const fetchAllUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/super-admin/all-users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data;
      if (Array.isArray(data)) {
        setUsers(data);
        setOrphanAdhesions([]);
      } else {
        setUsers(data.users || []);
        setOrphanAdhesions(data.orphanAdhesions || []);
      }
      setFilteredUsers(Array.isArray(data) ? data : (data.users || []));
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
      alert('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  // Fonctions pour les filtres
  const toggleStatusFilter = (status) => {
    setStatusFilter(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
    setShowStatusFilter(false);
  };

  const clearStatusFilter = () => {
    setStatusFilter([]);
  };

  const toggleServiceFilter = (service) => {
    setServiceFilter(prev => 
      prev.includes(service) 
        ? prev.filter(s => s !== service)
        : [...prev, service]
    );
    setShowServiceFilter(false);
  };

  const clearServiceFilter = () => {
    setServiceFilter([]);
  };

  // Fonction pour réinitialiser tous les filtres
  const resetAllFilters = () => {
    setStatusFilter([]);
    setYearFilter('');
    setServiceFilter([]);
    setSearchTerm('');
    setShowWithoutAdhesionOnly(false);
    setShowAdhesionsUserNotFound(false);
    setShowUsersOnly(true);
    
    // Fermer tous les dropdowns
    setShowStatusFilter(false);
    setShowYearFilter(false);
    setShowServiceFilter(false);
    
    // Supprimer les filtres sauvegardés
    localStorage.removeItem('superadmin_users_filters');
  };

  // Fonction d'export Excel
  const exportToExcel = () => {
    if (showAdhesionsUserNotFound) {
      exportAdhesionsToExcel(
        filteredOrphanAdhesions.map((a) => ({
          email: a.userId || '(user not found)',
          adhesions: [a],
        })),
        getStatusLabel,
        getServiceTypeLabel,
        getServiceStatusLabel
      );
      return;
    }
    exportAdhesionsToExcel(filteredUsers, getStatusLabel, getServiceTypeLabel, getServiceStatusLabel);
  };

  // Calculer les années disponibles
  const availableYears = [...new Set(
    users.flatMap(u => u.adhesions?.map(a => a.annee) || [])
  )].sort((a, b) => b - a);

  // Calculer les services disponibles
  const availableServices = [...new Set(
    users.flatMap(u => 
      u.adhesions?.flatMap(a => 
        a.services?.map(s => s.typeService) || []
      ) || []
    )
  )];

  // Calculer les statuts disponibles
  const availableStatuses = [...new Set(
    users.flatMap(u => u.adhesions?.map(a => a.status) || [])
  )];

  useEffect(() => {
    let filtered = users;

    // Filtre par recherche
    if (searchTerm) {
      filtered = filtered.filter(u => {
        const nom = u.nom || '';
        const prenom = u.prenom || '';
        const email = u.email || '';
        return nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
               prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
               email.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Filtre par statut d'adhésion
    if (statusFilter.length > 0) {
      filtered = filtered.filter(u => 
        u.adhesions?.some(a => statusFilter.includes(a.status))
      );
    }

    // Filtre par année
    if (yearFilter) {
      filtered = filtered.filter(u => 
        u.adhesions?.some(a => a.annee === parseInt(yearFilter))
      );
    }

    // Filtre par services
    if (serviceFilter.length > 0) {
      filtered = filtered.filter(u => 
        u.adhesions?.some(a => 
          a.services?.some(s => serviceFilter.includes(s.typeService))
        )
      );
    }

    if (showWithoutAdhesionOnly) {
      filtered = filtered.filter(u => !u.hasAdhesions);
    }

    if (showUsersOnly) {
      filtered = filtered.filter(u => u.roles && u.roles.includes('user'));
    }

    setFilteredUsers(filtered);
  }, [searchTerm, showWithoutAdhesionOnly, showUsersOnly, users, statusFilter, yearFilter, serviceFilter]);

  const renderUserPhones = (u) => {
    const fixe   = typeof u.telephone       === 'string' ? u.telephone.trim()       : '';
    const mobile = typeof u.telephoneMobile === 'string' ? u.telephoneMobile.trim() : '';
    if (!fixe && !mobile) return <span className="text-sm text-slate-400">-</span>;
    return (
      <div className="flex flex-col gap-1 text-sm text-slate-700">
        {fixe   && <span className="inline-flex items-center gap-1.5" title="Téléphone fixe"><PhoneIcon className="h-4 w-4 shrink-0 text-slate-400" /><span>{fixe}</span></span>}
        {mobile && <span className="inline-flex items-center gap-1.5" title="Téléphone mobile"><DevicePhoneMobileIcon className="h-4 w-4 shrink-0 text-slate-400" /><span>{mobile}</span></span>}
      </div>
    );
  };

  const handleEditClick = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/super-admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingUser(response.data);
      setEditForm({
        prenom: response.data.prenom || '',
        nom: response.data.nom || '',
        email: response.data.email || '',
        telephone: response.data.telephone || '',
        role: response.data.role || 'user',
      });
      setShowEditModal(true);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'utilisateur:', error);
      alert('Erreur lors du chargement des données');
    }
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/super-admin/users/${editingUser._id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Utilisateur mis à jour avec succès');
      setShowEditModal(false);
      setEditingUser(null);
      fetchAllUsers();
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert(error.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };


  const copyUserId = async (userId) => {
    try {
      await navigator.clipboard.writeText(userId);
      // Optionnel: afficher une notification temporaire
      const originalTitle = document.title;
      document.title = '✓ ID copié';
      setTimeout(() => {
        document.title = originalTitle;
      }, 1000);
    } catch (error) {
      console.error('Erreur lors de la copie:', error);
      // Fallback pour les navigateurs plus anciens
      const textArea = document.createElement('textarea');
      textArea.value = userId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const copyAdhesionId = async (adhesionId) => {
    try {
      await navigator.clipboard.writeText(adhesionId);
      const originalTitle = document.title;
      document.title = '✓ ID adhésion copié';
      setTimeout(() => {
        document.title = originalTitle;
      }, 1000);
    } catch (error) {
      console.error('Erreur lors de la copie:', error);
      const textArea = document.createElement('textarea');
      textArea.value = adhesionId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const viewAdhesion = (adhesionId) => {
    navigate(`/adherents/${adhesionId}`);
  };

  // Fonctions utilitaires comme dans Users.js
  const getServiceTypeLabel = (type) => {
    const labels = {
      'miellerie': 'Miellerie',
      'assurance_unaf': 'Services de l\'UNAF'
    };
    return labels[type] || type;
  };

  const getServiceStatusLabel = (status) => {
    const labels = {
      'actif': 'Actif',
      'en_attente_validation': 'En attente de validation',
      'en_attente_paiement': 'Paiement en attente',
      'en_attente_caution': 'Caution en attente',
      'expire': 'Expiré',
      'refuse': 'Refusé',
      'suspendu': 'Suspendu',
      'annule': 'Annulé'
    };
    return labels[status] || status;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'en_attente': 'En attente',
      'paiement_demande': 'Paiement demandé',
      'actif': 'Actif',
      'refuse': 'Refusé',
      'expiree': 'Expirée',
      'abandonnee': 'Abandonnée',
    };
    return labels[status] || status;
  };

  const getYearColor = (annee, status) => {
    const currentYear = new Date().getFullYear();
    
    if (status === 'actif') {
      return 'bg-green-100 text-green-800';
    }
    
    return annee < currentYear ? 'bg-gray-100 text-gray-600' : 'bg-honey-100 text-honey-800';
  };

  const matchesOrphanSearch = (a) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      String(a._id || '').toLowerCase().includes(q) ||
      String(a.userId || '').toLowerCase().includes(q) ||
      String(a.organisme || '').toLowerCase().includes(q) ||
      String(a.status || '').toLowerCase().includes(q) ||
      String(a.annee || '').includes(q)
    );
  };

  const filteredOrphanAdhesions = orphanAdhesions.filter(matchesOrphanSearch);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Tous les utilisateurs</h1>
        <p className="text-gray-600 mt-2">Liste complète de tous les utilisateurs du système</p>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="mb-6 space-y-4">
        {/* Première ligne - Filtres dropdowns */}
        <div className="flex flex-wrap gap-4">
          {/* Filtre Statut */}
          <div className="relative">
            <button
              onClick={() => setShowStatusFilter(!showStatusFilter)}
              className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium transition-colors ${
                statusFilter.length > 0
                  ? 'bg-honey-50 text-honey-700 border-honey-300'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Statut
              {statusFilter.length > 0 && (
                <span className="ml-2 bg-honey-100 text-honey-800 text-xs px-2 py-0.5 rounded-full">
                  {statusFilter.length}
                </span>
              )}
            </button>

            {showStatusFilter && (
              <div className="absolute z-10 mt-1 w-56 bg-white border border-gray-300 rounded-md shadow-lg">
                <div className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Statut d'adhésion</span>
                    {statusFilter.length > 0 && (
                      <button
                        onClick={clearStatusFilter}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Effacer
                      </button>
                    )}
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {availableStatuses.map((status) => (
                      <button
                        key={status}
                        onClick={() => toggleStatusFilter(status)}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-gray-50 rounded-md"
                      >
                        <span>{getStatusLabel(status)}</span>
                        {statusFilter.includes(status) && (
                          <CheckIcon className="h-4 w-4 text-green-600" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Filtre Année */}
          <div className="relative">
            <button
              onClick={() => setShowYearFilter(!showYearFilter)}
              className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium transition-colors ${
                yearFilter
                  ? 'bg-honey-50 text-honey-700 border-honey-300'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Année
              {yearFilter && (
                <span className="ml-2 bg-honey-100 text-honey-800 text-xs px-2 py-0.5 rounded-full">
                  {yearFilter}
                </span>
              )}
            </button>

            {showYearFilter && (
              <div className="absolute z-10 mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg">
                <div className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Année d'adhésion</span>
                    {yearFilter && (
                      <button
                        onClick={() => { setYearFilter(''); setShowYearFilter(false); }}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Effacer
                      </button>
                    )}
                  </div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {availableYears.map((year) => (
                      <button
                        key={year}
                        onClick={() => { setYearFilter(year.toString()); setShowYearFilter(false); }}
                        className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-50 rounded-md ${
                          yearFilter === year.toString() ? 'bg-honey-50 text-honey-700' : ''
                        }`}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Filtre Services */}
          <div className="relative">
            <button
              onClick={() => setShowServiceFilter(!showServiceFilter)}
              className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium transition-colors ${
                serviceFilter.length > 0
                  ? 'bg-honey-50 text-honey-700 border-honey-300'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Services
              {serviceFilter.length > 0 && (
                <span className="ml-2 bg-honey-100 text-honey-800 text-xs px-2 py-0.5 rounded-full">
                  {serviceFilter.length}
                </span>
              )}
            </button>

            {showServiceFilter && (
              <div className="absolute z-10 mt-1 w-56 bg-white border border-gray-300 rounded-md shadow-lg">
                <div className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Types de services</span>
                    {serviceFilter.length > 0 && (
                      <button
                        onClick={clearServiceFilter}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Effacer
                      </button>
                    )}
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {availableServices.map((service) => (
                      <button
                        key={service}
                        onClick={() => toggleServiceFilter(service)}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-gray-50 rounded-md"
                      >
                        <span>{getServiceTypeLabel(service)}</span>
                        {serviceFilter.includes(service) && (
                          <CheckIcon className="h-4 w-4 text-green-600" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bouton Réinitialiser les filtres */}
          {(statusFilter.length > 0 || yearFilter || serviceFilter.length > 0 || searchTerm || showWithoutAdhesionOnly || showAdhesionsUserNotFound) && (
            <button
              onClick={resetAllFilters}
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
            >
              <XMarkIcon className="h-4 w-4 mr-2" />
              Réinitialiser les filtres
            </button>
          )}

          {/* Bouton Exporter */}
          <button
            onClick={exportToExcel}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            Exporter Excel
          </button>
        </div>

        {/* Deuxième ligne - Recherche et checkboxes */}
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, prénom ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <label className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={showWithoutAdhesionOnly}
              onChange={(e) => {
                setShowWithoutAdhesionOnly(e.target.checked);
                if (e.target.checked) setShowAdhesionsUserNotFound(false);
              }}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700 whitespace-nowrap">Utilisateurs sans adhésion</span>
          </label>
          <label className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={showAdhesionsUserNotFound}
              onChange={(e) => {
                setShowAdhesionsUserNotFound(e.target.checked);
                if (e.target.checked) setShowWithoutAdhesionOnly(false);
              }}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700 whitespace-nowrap">Adhésions user not found</span>
          </label>
          <label className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={showUsersOnly}
              onChange={(e) => setShowUsersOnly(e.target.checked)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700 whitespace-nowrap">Adhérents uniquement</span>
          </label>
          
          {/* Results count */}
          <div className="text-sm text-gray-500">
            {showAdhesionsUserNotFound
              ? `${filteredOrphanAdhesions.length} adhésion${filteredOrphanAdhesions.length > 1 ? 's' : ''}`
              : `${filteredUsers.length} utilisateur${filteredUsers.length > 1 ? 's' : ''}`}
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total utilisateurs</div>
          <div className="text-2xl font-bold text-gray-900">{users.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Administrateurs</div>
          <div className="text-2xl font-bold text-indigo-600">
            {users.filter(u => u.role === 'admin' || u.role === 'super_admin').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Utilisateurs standards</div>
          <div className="text-2xl font-bold text-green-600">
            {users.filter(u => u.role === 'user').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Sans adhésion</div>
          <div className="text-2xl font-bold text-honey-600">
            {users.filter(u => !u.hasAdhesions).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Adhésions user not found</div>
          <div className="text-2xl font-bold text-red-600">
            {orphanAdhesions.length}
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          {showAdhesionsUserNotFound ? (
            <>
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Adhésion
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      User ID (absent)
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider hidden sm:table-cell">
                      Motif
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider hidden lg:table-cell">
                      Création
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {filteredOrphanAdhesions.map((adhesion) => (
                    <tr key={adhesion._id} className="hover:bg-red-50/40">
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-bold text-slate-900">{adhesion.organisme}</span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getYearColor(adhesion.annee, adhesion.status)}`}>
                            <span className="text-xs font-bold">{adhesion.annee}</span>
                            <span className="text-xs text-slate-400 px-1.5 pb-0.5">|</span>
                            {getStatusLabel(adhesion.status)}
                          </span>
                          <span className="text-xs text-slate-500">
                            {adhesion.nombreRuches ? `${adhesion.nombreRuches} ruches` : 'Nombre de ruches non renseigné'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {adhesion.userId ? (
                          <div className="flex items-center gap-2">
                            <code className="text-xs text-red-700 break-all">{adhesion.userId}</code>
                            <button
                              onClick={() => copyUserId(adhesion.userId)}
                              className="p-1 border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-50"
                              title="Copier l'ID user"
                            >
                              <ClipboardIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-sm text-red-600">Aucun user (null)</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center hidden sm:table-cell">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          {adhesion.reason === 'user_null' ? 'user null' : 'user not found'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-slate-500 hidden lg:table-cell">
                        {adhesion.createdAt ? new Date(adhesion.createdAt).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => viewAdhesion(adhesion._id)}
                            className="inline-flex gap-2 items-center py-1.5 pl-1.5 pr-2 border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                            title="Voir les détails de l'adhésion"
                          >
                            <EyeIcon className="h-3 w-3" />
                            Voir adhésion
                          </button>
                          <button
                            onClick={() => copyAdhesionId(adhesion._id)}
                            className="inline-flex items-center p-1.5 border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                            title="Copier l'ID de l'adhésion"
                          >
                            <ClipboardIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredOrphanAdhesions.length === 0 && (
                <div className="text-center py-12">
                  <UserIcon className="mx-auto h-12 w-12 text-slate-400" />
                  <h3 className="mt-2 text-sm font-medium text-slate-900">Aucune adhésion mal liée</h3>
                  <p className="mt-1 text-sm text-slate-500">Toutes les adhésions pointent vers un utilisateur existant.</p>
                </div>
              )}
            </>
          ) : (
            <>
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden lg:table-cell">
                  Contacts
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider hidden sm:table-cell">
                  Adhésions
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider hidden lg:table-cell">
                  Création
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {filteredUsers.map((u) => (
                <React.Fragment key={u._id}>
                  {/* Ligne principale de l'utilisateur */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                          {u.typePersonne === 'personne_physique' || !u.typePersonne
                            ? <UserIcon className="h-5 w-5 text-indigo-600" />
                            : <BuildingOfficeIcon className="h-5 w-5 text-indigo-600" />}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-slate-900">
                            {u.raisonSociale
                              ? <>{u.raisonSociale} ({u.designation ? `${u.designation} ` : ''}{u.prenom} {u.nom})</>
                              : <>{u.designation ? `${u.designation} ` : ''}{u.prenom} {u.nom}</>}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            {u.role === 'super_admin' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Super Admin</span>
                            )}
                            {u.role === 'admin' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">Admin</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-middle hidden lg:table-cell">
                      <div className="flex items-center gap-2 text-sm text-slate-900">
                        <EnvelopeIcon className="h-4 w-4 text-slate-400 shrink-0" />
                        {u.email}
                      </div>
                      {renderUserPhones(u)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center hidden sm:table-cell">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        {u.adhesions?.length || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-slate-500 hidden lg:table-cell">
                      {new Date(u.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      <div className="flex items-end justify-end space-x-2">
                        <button
                          onClick={() => handleEditClick(u._id)}
                          className="flex gap-2 items-center py-1.5 pl-1.5 pr-2 border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                          title="Éditer l'utilisateur"
                        >
                          <PencilIcon className="h-4 w-4" />
                          <span className="text-xs">Modifier</span>
                        </button>
                        <button
                          onClick={() => copyUserId(u._id)}
                          className="flex gap-2 items-center p-1.5 border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                          title="Copier l'ID utilisateur"
                        >
                          <ClipboardIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Lignes des adhésions */}
                  {u.adhesions?.map((adhesion, idx) => (
                    <React.Fragment key={`${u._id}-${adhesion._id}`}>
                      <tr className="bg-slate-50">
                        <td className="py-3 whitespace-nowrap">
                          <div className="flex items-center pl-8">
                            <div>
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <span className="text-sm font-bold text-slate-900">{adhesion.organisme}</span>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getYearColor(adhesion.annee, adhesion.status)}`}>
                                  <span className="text-xs font-bold">{adhesion.annee}</span>
                                  <span className="text-xs text-slate-400 px-1.5 pb-0.5">|</span>
                                  {getStatusLabel(adhesion.status)}
                                </span>
                                <span className="text-xs text-slate-500">
                                  {adhesion.nombreRuches ? `${adhesion.nombreRuches} ruches` : 'Nombre de ruches non renseigné'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap hidden lg:table-cell" />
                        <td className="px-6 py-3 whitespace-nowrap hidden sm:table-cell" />
                        <td className="px-6 py-3 whitespace-nowrap text-center text-xs text-slate-500 hidden lg:table-cell">
                          {new Date(adhesion.createdAt).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-xs text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => viewAdhesion(adhesion._id)}
                              className="inline-flex gap-2 items-center py-1.5 pl-1.5 pr-2 border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                              title="Voir les détails de l'adhésion"
                            >
                              <EyeIcon className="h-3 w-3" />
                              Voir adhésion
                            </button>
                            <button
                              onClick={() => copyAdhesionId(adhesion._id)}
                              className="inline-flex items-center p-1.5 border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                              title="Copier l'ID de l'adhésion"
                            >
                              <ClipboardIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Lignes des services */}
                      {adhesion.services?.map((service) => (
                        <tr key={`${u._id}-${adhesion._id}-${service._id}`} className="bg-slate-50">
                          <td className="py-3 whitespace-nowrap">
                            <div className="flex items-center pl-8">
                              <div className="">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-lg text-slate-400 pb-2">&#x21B3;</span>
                                  <span className="text-sm font-bold text-slate-900">
                                    {getServiceTypeLabel(service.typeService)}
                                  </span>
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getYearColor(service.annee ?? adhesion.annee, service.status)}`}>
                                    <span className="text-xs font-bold">{service.annee ?? adhesion.annee}</span>
                                    <span className="text-xs text-slate-400 px-1.5 pb-0.5">|</span>
                                    {getServiceStatusLabel(service.status)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap hidden lg:table-cell" />
                          <td className="px-6 py-2 whitespace-nowrap hidden sm:table-cell" />
                          <td className="px-6 py-2 whitespace-nowrap text-center text-xs text-slate-500 hidden lg:table-cell">
                            {service.createdAt ? new Date(service.createdAt).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-xs text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => viewAdhesion(adhesion._id)}
                                className="inline-flex gap-2 items-center py-1.5 pl-1.5 pr-2 border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                                title="Voir les détails du service"
                              >
                                <EyeIcon className="h-3 w-3" />
                                Voir adhésion
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          
          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <UserIcon className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-2 text-sm font-medium text-slate-900">Aucun utilisateur trouvé</h3>
              <p className="mt-1 text-sm text-slate-500">Essayez de modifier vos critères de recherche.</p>
            </div>
          )}
            </>
          )}
        </div>
      </div>

      {/* Modale d'édition */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Modifier l'utilisateur
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {editingUser.prenom} {editingUser.nom}
                </p>
              </div>
              <button
                onClick={() => { setShowEditModal(false); setEditingUser(null); }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                  <input
                    type="text"
                    value={editForm.prenom || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, prenom: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                  <input
                    type="text"
                    value={editForm.nom || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, nom: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={editForm.email || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input
                    type="text"
                    value={editForm.telephone || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, telephone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                  <select
                    value={editForm.role || 'user'}
                    onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="user">Utilisateur</option>
                    <option value="admin">Administrateur</option>
                    <option value="super_admin">Super Administrateur</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => { setShowEditModal(false); setEditingUser(null); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveUser}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
